/**
 * 网络检测工具函数
 * 直接返回浏览器网络连接信息的 JSON 字符串
 */

/**
 * 获取用户当前的网络连接信息
 *
 * @returns 网络连接信息的 JSON 字符串
 * - 成功时返回包含所有可用网络属性的 JSON 字符串
 * - 失败时返回 '{"error": "不支持或获取失败"}'
 */
export const getNetworkType = (): string => {
    try {
        // 步骤 1: 获取浏览器网络连接 API
        // @ts-ignore - NetworkInformation API 类型定义可能不完整
        const connection = window.navigator?.connection;

        // 步骤 2: 检查 API 是否可用
        if (!connection) {
            return '{"error": "不支持"}';
        }

        // 步骤 3: 将连接对象转换为 JSON 字符串
        const networkInfo = {
            // 网络连接类型：表示设备当前使用的网络连接类型
            // 可能的值：'bluetooth', 'cellular', 'ethernet', 'none', 'wifi', 'wimax', 'other', 'unknown'
            // 例如：'wifi' 表示 WiFi 连接，'cellular' 表示移动网络
            type: connection.type || null,

            // 有效连接类型：基于实际网络性能估算的连接类型
            // 可能的值：'slow-2g', '2g', '3g', '4g'
            // 这个值会根据网络的实际速度和质量动态调整，比 type 更准确地反映用户体验
            // 例如：即使连接的是 4G 网络，如果信号很差，effectiveType 可能是 '2g'
            effectiveType: connection.effectiveType || null,

            // 下行带宽：表示网络的下行传输速度，单位为 Mbps（兆比特每秒）
            // 这个值表示从服务器到设备的数据传输速度
            // 例如：10.5 表示下行速度为 10.5 Mbps
            downlink: connection.downlink || null,

            // 上行带宽：表示网络的上行传输速度，单位为 Mbps（兆比特每秒）
            // 这个值表示从设备到服务器的数据传输速度
            // 例如：2.1 表示上行速度为 2.1 Mbps
            uplink: connection.uplink || null,

            // 往返时间：Round Trip Time，表示数据包从设备发送到服务器再返回所需的时间
            // 单位为毫秒（ms），这个值越小表示网络延迟越低
            // 例如：50 表示网络延迟为 50 毫秒
            rtt: connection.rtt || null,

            // 数据节省模式：表示用户是否启用了数据节省模式
            // 当用户启用数据节省模式时，浏览器会尝试减少数据使用量
            // 值为 true 时表示启用了数据节省模式，false 表示未启用
            saveData: connection.saveData || false,
        };

        return JSON.stringify(networkInfo);
    } catch (error) {
        // 步骤 4: 异常处理, 确保函数稳定性
        console.warn('Failed to get network info:', error);
        const message = error instanceof Error ? error.message : String(error);
        return `{"error": "获取失败: ${message}"}`;
    }
};

/** 聚合后的网络状态三分类（无网 / Wi‑Fi / 蜂窝数据），与详情页口径一致 */
export type NetworkStatusLabel = '无网' | 'WiFi' | '数据流量';

/**
 * 获取当前网络状态（三分类），与详情页 `getNetworkStatus` 一致
 */
export const getNetworkStatus = (): NetworkStatusLabel => {
    try {
        if (typeof navigator === 'undefined') {
            return '无网';
        }
        if (!navigator.onLine) {
            return '无网';
        }
        // @ts-expect-error NetworkInformation API
        const connection = window.navigator?.connection;
        const type: string | undefined = connection?.type;

        if (type === 'none') {
            return '无网';
        }
        if (type === 'cellular') {
            return '数据流量';
        }
        return 'WiFi';
    } catch {
        return 'WiFi';
    }
};
